import React from "react";
import { Box, Typography } from "@mui/material";
import { tokens } from "../../theme";
import { getOrderStatusStyle, getTicketStatusStyle } from "../../constants/statusStyles";
import { orderStatus } from "../../constants/orderStatus";
import { ticketStatus } from "../../constants/ticketStatus";
import { IconArrowUpRight } from "./icons";

const numberStyle = { fontVariantNumeric: "tabular-nums", letterSpacing: "-0.018em" };

/** Chip de estado de orden. Un color por estado, igual en tabla, lista y gráfico. */
export const OrderStatusChip = ({ status, size = "md" }) => {
  const style = getOrderStatusStyle(status);
  const label = orderStatus.find((s) => s.value === status)?.label || "";
  return <Pill label={label} color={style.text} background={style.bg} size={size} />;
};

/** Chip de estado de factura. */
export const TicketStatusChip = ({ status, size = "md" }) => {
  const style = getTicketStatusStyle(status);
  const label = ticketStatus.find((s) => s.value === status)?.label || "";
  return <Pill label={label} color={style.text} background={style.bg} size={size} />;
};

export const Pill = ({ label, color, background, size = "md" }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      borderRadius: 999,
      whiteSpace: "nowrap",
      fontWeight: 500,
      fontSize: size === "sm" ? "11px" : "11.5px",
      padding: size === "sm" ? "2px 8px" : "3px 9px",
      color,
      backgroundColor: background,
    }}
  >
    {label}
  </Box>
);

/** Cuadrito de ícono con tinte, usado en tarjetas y accesos directos. */
export const IconTile = ({ icon: Icon, color, background, size = 26, iconSize = 14, radius = 7 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: `${radius}px`,
      backgroundColor: background,
      color,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Icon size={iconSize} />
  </Box>
);

/**
 * Tarjeta de métrica: título, cifra grande, una línea de contexto y un pie accionable.
 * `footer` es el paso siguiente — sin acción, la métrica no sirve para nada.
 */
export const MetricCard = ({
  label,
  icon,
  iconColor,
  iconBackground,
  value,
  hint,
  hintColor,
  footer,
  footerIcon: FooterIcon = IconArrowUpRight,
  footerColor = tokens.brandDark,
  onClick,
  compact = false,
}) => (
  <Box
    onClick={onClick}
    sx={{
      backgroundColor: tokens.surface,
      border: `1px solid ${tokens.border}`,
      borderRadius: `${tokens.radiusCard}px`,
      padding: compact ? "13px 13px 12px" : "16px 18px 14px",
      display: "flex",
      flexDirection: "column",
      gap: compact ? "9px" : "12px",
      cursor: onClick ? "pointer" : "default",
      transition: "border-color 120ms ease, box-shadow 120ms ease",
      "&:hover": onClick
        ? { borderColor: tokens.borderStrong, boxShadow: tokens.shadowSm }
        : undefined,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
      {icon && (
        <IconTile
          icon={icon}
          color={iconColor}
          background={iconBackground}
          size={compact ? 24 : 26}
          iconSize={compact ? 13 : 14}
        />
      )}
      <Typography
        sx={{
          fontSize: compact ? "11.5px" : "12.5px",
          fontWeight: 500,
          color: tokens.ink2,
          lineHeight: 1.25,
        }}
      >
        {label}
      </Typography>
    </Box>

    <Box sx={{ display: "flex", flexDirection: "column", gap: compact ? "3px" : "5px" }}>
      <Typography
        sx={{
          ...numberStyle,
          fontSize: compact ? "22px" : "27px",
          fontWeight: 600,
          lineHeight: 1,
          color: tokens.ink,
        }}
      >
        {value}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: compact ? "11.5px" : "12px", color: hintColor || tokens.ink3 }}>
          {hint}
        </Typography>
      )}
    </Box>

    {footer && !compact && (
      <Box
        sx={{
          borderTop: `1px solid ${tokens.borderSoft}`,
          paddingTop: "10px",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          fontSize: "12.5px",
          fontWeight: 500,
          color: footerColor,
        }}
      >
        {footer}
        <FooterIcon size={13} />
      </Box>
    )}
  </Box>
);

/** Tarjeta contenedora con encabezado y acción opcional a la derecha. */
export const SectionCard = ({ title, subtitle, action, onAction, children, padded = true, sx }) => (
  <Box
    sx={{
      backgroundColor: tokens.surface,
      border: `1px solid ${tokens.border}`,
      borderRadius: `${tokens.radiusCard}px`,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      minWidth: 0,
      ...sx,
    }}
  >
    {(title || action) && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: padded ? "15px 18px 13px" : "15px 16px 13px",
          borderBottom: children ? `1px solid ${tokens.borderSoft}` : "none",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", flexGrow: 1, minWidth: 0 }}>
          {title && <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>{title}</Typography>}
          {subtitle && (
            <Typography sx={{ fontSize: "12px", color: tokens.ink3 }}>{subtitle}</Typography>
          )}
        </Box>
        {action && (
          <Box
            onClick={onAction}
            sx={{
              fontSize: "12.5px",
              fontWeight: 500,
              color: tokens.brandDark,
              cursor: onAction ? "pointer" : "default",
              whiteSpace: "nowrap",
            }}
          >
            {action}
          </Box>
        )}
      </Box>
    )}
    {children}
  </Box>
);

/** Acceso directo: ícono con tinte + etiqueta. Alto mínimo de 44 px para el pulgar. */
export const QuickAction = ({ icon, label, color, background, onClick, stacked = false }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      flexDirection: stacked ? "column" : "column",
      alignItems: stacked ? "center" : "flex-start",
      gap: stacked ? "7px" : "8px",
      padding: stacked ? "11px 6px 9px" : "12px 12px 11px",
      minHeight: stacked ? 76 : 62,
      justifyContent: "center",
      border: `1px solid ${tokens.border}`,
      borderRadius: stacked ? `${tokens.radiusCard}px` : "10px",
      backgroundColor: tokens.surface,
      cursor: "pointer",
      transition: "border-color 120ms ease, box-shadow 120ms ease",
      "&:hover": { borderColor: tokens.borderStrong, boxShadow: tokens.shadowSm },
    }}
  >
    <IconTile
      icon={icon}
      color={color}
      background={background}
      size={stacked ? 30 : 28}
      iconSize={stacked ? 16 : 15}
      radius={stacked ? 999 : 8}
    />
    <Typography
      sx={{
        fontSize: stacked ? "10.5px" : "13px",
        fontWeight: 500,
        lineHeight: 1.25,
        textAlign: stacked ? "center" : "left",
        color: tokens.ink,
      }}
    >
      {label}
    </Typography>
  </Box>
);

/** Cifra tabular, para importes y contadores. */
export const Num = ({ children, sx, ...rest }) => (
  <Box component="span" sx={{ ...numberStyle, ...sx }} {...rest}>
    {children}
  </Box>
);

export const numeric = numberStyle;
